import React from 'react';
import { IntlService } from 'external-services/intl-service/index';
import style from './style.module.scss';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IBookingOptionProps, IBookingOptionsProps, classPrefix } from './types';
import {
  createStyles,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  makeStyles,
  Typography
} from '@material-ui/core';


/** Styles for accordion */
const useStyles = makeStyles(() =>
  createStyles({
    root: {
      [`& .${classPrefix}-MuiExpansionPanel-root`]: {
        marginBottom: '10px'
      },
      [`& .${classPrefix}-MuiExpansionPanel-root:before`]: {
        opacity: '0'
      },
      [`& .${classPrefix}-MuiExpansionPanelSummary-root`]: {
        padding: '0'
      },
      [`& .${classPrefix}-MuiExpansionPanelSummary-root.Mui-expanded`]: {
        minHeight: '40px'
      },
      [`& .${classPrefix}-MuiExpansionPanelSummary-content`]: {
        margin: '0'
      },
      [`& .${classPrefix}-MuiIconButton-root`]: {
        padding: '0px 12px'
      },
      [`& .${classPrefix}-MuiExpansionPanelDetails-root`]: {
        padding: '0px 10px 8px',
        display: 'block',
        flexDirection: 'row'
      },
      [`& .${classPrefix}-MuiPaper-root`]: {
        color: 'rgba(0, 0, 0, 0.54)'
      }
    }
  })
);

/**
 * 1. Renders booking options alongwith extras
 * 2. Display the booking options inside an accordion, when the number of booking options are 4 or more
 */
export const BookingOptions = (props: IBookingOptionsProps) => {

  const { linesOccupiedByBookingOptions, bookingOptionList, isUpsell } = props;

  // Do not proceed, when no booking options are provided
  if (!bookingOptionList || bookingOptionList.length === 0) return null;

  return (
    <>
      {
        linesOccupiedByBookingOptions > 3 ?
          <BookingOptionExpansionPanel bookingOptionList={bookingOptionList.filter(o=>o.isUpsellItem === isUpsell)} {...props} /> :
          <>
            <p className={style.summaryLabel}>{isUpsell ? 'Upsell Options' : 'Booking Options'}</p>
            <BookingOptionList {...props} />
            <hr className={style.summaryHorizontalLine} />
          </>
      }
    </>
  )
}

/** Display booking options in accordion view */
const BookingOptionExpansionPanel = (props: IBookingOptionsProps) => {

  const classes = useStyles();

  return (
    <div className={classes.root}>
      <ExpansionPanel square={true}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <p className={style.bookingOptionHeader}>{props.isUpsell ? 'Upsell Options' : 'Booking Options'}</p>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <BookingOptionList {...props} />
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </div>
  )
}

/** Renders the booking option list */
const BookingOptionList = (props: IBookingOptionsProps) => {

  const bookingOptionList = props.bookingOptionList.filter(t => t.isUpsellItem === props.isUpsell).map((option, index) => {
    return (
      <div key={index}>
        <Typography component={"div"} >
          <BookingOption bookingOption={option} hasExtras={false} currency={props.currency} />
        </Typography>
        {
          option.extras.map((extra, index) => <BookingOption key={index} bookingOption={extra} hasExtras={true} currency={props.currency}/>)

        }
      </div>
    );
  });

  return <>{bookingOptionList}</>;
}

/** Renders a booking option */
const BookingOption = (props: IBookingOptionProps) => {

  const {bookingOption, hasExtras, currency} = props;

  if (bookingOption.quantity === 0) return null;

  const quantities = bookingOption.quantity > 1 ? " x " + bookingOption.quantity : "";

  // Prefix with a plus sign to extras
  const plusSign = hasExtras ? " + " : "";

  return <div className={style.bookingOption}>{`${plusSign}${bookingOption.name} ${IntlService.currencyValueAsString(bookingOption.price, currency)}${quantities}`}</div>
}
